/**
 * Export et import de la base.
 *
 * Les donnees ne quittent jamais l'appareil autrement que par ce fichier, que
 * l'utilisateur declenche et range lui-meme. C'est aussi la seule protection
 * contre la perte du telephone : il n'y a pas de serveur pour rien restaurer.
 *
 * ## Les pieces jointes sont optionnelles
 *
 * Encodees en base64, elles gonflent d'un tiers et peuvent faire passer le
 * fichier de 200 Ko a 30 Mo. Une sauvegarde hebdomadaire legere a plus de
 * chances d'etre faite qu'une sauvegarde complete penible : l'ecran propose les
 * deux et annonce le poids avant d'exporter.
 */

import { nowInstant } from '../domain/temps'
import type { Piece, Reglages } from '../domain/types'
import { META_DERNIER_EXPORT, META_MOI, db, ecrireMeta, lireMeta } from './db'
import { chargerDonnees, lireReglages } from './repo'
import type { Donnees } from '../analysis/donnees'

const FORMAT = 'budget-chf'
const VERSION = 1

type PieceSerialisee = Omit<Piece, 'blob'> & { base64: string }

export interface Sauvegarde {
  format: string
  version: number
  exporteLe: string
  reglages: Reglages
  donnees: Omit<Donnees, 'moiId'> & { moiId: string }
  pieces: PieceSerialisee[]
}

export async function exporter(inclurePieces: boolean): Promise<Sauvegarde> {
  const [donnees, reglages] = await Promise.all([chargerDonnees(), lireReglages()])
  const pieces: PieceSerialisee[] = []
  if (inclurePieces) {
    for (const p of await db.pieces.toArray()) {
      const { blob, ...meta } = p
      pieces.push({ ...meta, base64: await versBase64(blob) })
    }
  }
  await ecrireMeta(META_DERNIER_EXPORT, nowInstant())
  return { format: FORMAT, version: VERSION, exporteLe: nowInstant(), reglages, donnees, pieces }
}

export function nomFichierExport(complet: boolean): string {
  return `budget-${nowInstant().slice(0, 10)}${complet ? '-complet' : ''}.json`
}

export async function dateDernierExport(): Promise<string | null> {
  return lireMeta<string | null>(META_DERNIER_EXPORT, null)
}

/** Poids approximatif des pieces jointes une fois encodees, pour prevenir. */
export async function poidsPiecesEncodees(): Promise<number> {
  let octets = 0
  await db.pieces.each((p) => {
    octets += p.octets
  })
  return Math.ceil((octets * 4) / 3)
}

export interface ResultatImport {
  operations: number
  pieces: number
}

export class SauvegardeInvalide extends Error {}

export function validerSauvegarde(brut: unknown): Sauvegarde {
  const s = brut as Partial<Sauvegarde>
  if (!s || typeof s !== 'object' || s.format !== FORMAT) {
    throw new SauvegardeInvalide("Ce fichier n'est pas une sauvegarde de cette application.")
  }
  if (typeof s.version !== 'number' || s.version > VERSION) {
    throw new SauvegardeInvalide('Cette sauvegarde vient d’une version plus récente de l’application : mettez-la à jour d’abord.')
  }
  const d = s.donnees as Partial<Donnees> | undefined
  const tables: Array<keyof Donnees> = ['comptes', 'categories', 'tiers', 'operations', 'recurrences', 'exceptions', 'modeles', 'parts', 'reglements', 'budgets']
  if (!d || tables.some((t) => !Array.isArray(d[t]))) {
    throw new SauvegardeInvalide('Sauvegarde incomplète ou endommagée.')
  }
  return s as Sauvegarde
}

/**
 * Restaure une sauvegarde.
 *
 * `remplacer` vide d'abord la base : c'est le cas d'un nouveau telephone. Sans
 * lui, les enregistrements sont fusionnes par identifiant — ceux de la
 * sauvegarde l'emportent, ceux qui n'y figurent pas sont conserves.
 *
 * Le tiers « moi » de la sauvegarde devient celui de la base : sans cela, deux
 * « moi » coexisteraient apres fusion et le reliquat des anciennes repartitions
 * irait a un tiers que plus rien ne reconnait.
 */
export async function importer(brut: unknown, remplacer: boolean): Promise<ResultatImport> {
  const s = validerSauvegarde(brut)
  const d = s.donnees
  const pieces: Piece[] = s.pieces.map(({ base64, ...meta }) => ({ ...meta, blob: depuisBase64(base64, meta.type) }))

  await db.transaction('rw', db.tables, async () => {
    if (remplacer) await Promise.all(db.tables.map((t) => t.clear()))
    else {
      // Fusion : l'ancien « moi » local cede la place a celui de la sauvegarde.
      const moiLocal = await lireMeta<string | null>(META_MOI, null)
      if (moiLocal && moiLocal !== d.moiId) {
        await db.parts.where('tiersId').equals(moiLocal).modify({ tiersId: d.moiId })
        await db.tiers.delete(moiLocal)
      }
    }
    await db.comptes.bulkPut(d.comptes)
    await db.categories.bulkPut(d.categories)
    await db.tiers.bulkPut(d.tiers)
    await db.operations.bulkPut(d.operations)
    await db.recurrences.bulkPut(d.recurrences)
    await db.exceptions.bulkPut(d.exceptions)
    await db.modeles.bulkPut(d.modeles)
    await db.parts.bulkPut(d.parts)
    await db.reglements.bulkPut(d.reglements)
    await db.budgets.bulkPut(d.budgets)
    if (pieces.length) await db.pieces.bulkPut(pieces)
    await ecrireMeta(META_MOI, d.moiId)
    await db.meta.put({ cle: 'reglages', valeur: s.reglages })
    await db.meta.put({ cle: 'initialiseeLe', valeur: s.exporteLe })
  })

  return { operations: d.operations.length, pieces: pieces.length }
}

// ---------------------------------------------------------------------------

async function versBase64(blob: Blob): Promise<string> {
  const octets = new Uint8Array(await blob.arrayBuffer())
  // Par tranches : `String.fromCharCode(...octets)` sur plusieurs Mo depasse la
  // taille maximale de la pile d'appels.
  let binaire = ''
  const TRANCHE = 0x8000
  for (let i = 0; i < octets.length; i += TRANCHE) {
    binaire += String.fromCharCode(...octets.subarray(i, i + TRANCHE))
  }
  return btoa(binaire)
}

function depuisBase64(b64: string, type: string): Blob {
  const binaire = atob(b64)
  const octets = new Uint8Array(binaire.length)
  for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i)
  return new Blob([octets], { type })
}
